// `vendor/custom-radios-checkboxes/custom-radios-checkboxes.js:4` — the `<radio>` element, the
// checkbox's sibling in the same vendored directive.
//
// Its compiled template, like the checkbox's, is a label wrapping an input and a drawn box:
//
//   <label class="ng-radio focused-{{guid}}">
//     <input type="radio" ng-model="…" value="…">
//     <div class="{caller's class} no-select"></div>
//     <span class="ng-transclude"> </span>
//   </label>
//
// ⚠️ AND IT PUTS THE CALLER'S CLASS ON THE INNER DIV, NOT THE LABEL — the opposite of the
// checkbox, which merges it onto the label through `replace: true`. The two directives sit
// twenty lines apart in one file and disagree, so the obvious "same as the checkbox" is wrong.
import { useId, useState, type ReactElement, type ReactNode } from 'react'

export interface WooRadioProps {
  name: string
  value: string
  checked: boolean
  disabled?: boolean
  /** Goes on the INNER div — see the note above. */
  className?: string
  onChange?: () => void
  children?: ReactNode
}

export function WooRadio({
  name,
  value,
  checked,
  disabled = false,
  className,
  onChange,
  children,
}: WooRadioProps): ReactElement {
  const id = useId()
  // `focused-{{guid}}` with `ng-focus="guid = true"` / `ng-blur="guid = false"`: the label reads
  // `focused-true` while its input has focus, and `_custom-radios-checkboxes.scss` colours the
  // drawn circle's border then. Only the `true` value is styled, so it is the only one rendered.
  const [focused, setFocused] = useState(false)
  return (
    <label className={focused ? 'ng-radio focused-true' : 'ng-radio'} htmlFor={id}>
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange?.()}
        onFocus={() => {
          setFocused(true)
        }}
        onBlur={() => {
          setFocused(false)
        }}
      />
      <div className={[className, 'no-select'].filter(Boolean).join(' ')} />
      <span className="ng-transclude"> {children}</span>
    </label>
  )
}
